import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Redirect, useHistory, useLocation } from "react-router-dom";
import axios from "axios";
import {
  FaCheck,
  FaClipboardList,
  FaHome,
  FaPencilAlt,
  FaPlay,
  FaPlus,
  FaSearch,
  FaSyncAlt,
  FaTasks,
  FaUserCheck,
} from "react-icons/fa";
import BackButton from "../ChildComponent/BackButton";
import MyLoader from "../Spinner/MyLoader";
import SessionManager from "../Utils/SessionManager";
import myToast from "../Utils/MyToast";
import MdlLessonPlanAssign, { flattenClasses } from "../dialog/MdlLessonPlanAssign";
import COLORS from "../resources/colors/COLORS";
import "../css/Admin_css/admin-customize.css";
import "../css/Admin_css/payroll.css";

const API_BASE = () =>
  `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;

const HERO_GRADIENT = `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 55%, ${COLORS.primary_color_shade9} 100%)`;

const TABS = [
  { id: "plans", label: "Lesson plans", icon: FaClipboardList },
  { id: "tasks", label: "Assignments", icon: FaTasks },
  { id: "mine", label: "My tasks", icon: FaUserCheck },
];

const STATUS_COLORS = {
  Draft: { bg: "#f1f5f9", fg: "#475569" },
  Submitted: { bg: "#dbeafe", fg: "#1d4ed8" },
  Approved: { bg: "#dcfce7", fg: "#15803d" },
  Published: { bg: "#e0e7ff", fg: "#4338ca" },
  Open: { bg: "#fef3c7", fg: "#b45309" },
  In_Progress: { bg: "#dbeafe", fg: "#1d4ed8" },
  Rejected: { bg: "#fee2e2", fg: "#b91c1c" },
  Closed: { bg: "#f1f5f9", fg: "#64748b" },
};

//Created By Cursor
function StatusPill({ value }) {
  const style = STATUS_COLORS[value] || { bg: "#f1f5f9", fg: "#334155" };
  return (
    <span
      className="lp-pill"
      style={{ background: style.bg, color: style.fg }}
    >
      {String(value || "").replace(/_/g, " ")}
    </span>
  );
}

//Created By Cursor
export default function LessonPlan() {
  const history = useHistory();
  const location = useLocation();
  const loggedIn = localStorage.getItem("token") != null;

  const initialTab = useMemo(() => {
    const p = new URLSearchParams(location.search);
    const t = p.get("tab");
    return TABS.some((x) => x.id === t) ? t : "plans";
  }, [location.search]);

  const [tab, setTab] = useState(initialTab);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [sessionId, setSessionId] = useState("");
  const [search, setSearch] = useState("");
  const [plans, setPlans] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [counts, setCounts] = useState({});
  const [classData, setClassData] = useState([]);
  const [classSubjects, setClassSubjects] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [assignOpen, setAssignOpen] = useState(false);
  const [busyId, setBusyId] = useState("");

  const staffDetails = SessionManager.getStaffDetails();
  const myStaffId = staffDetails?.staff_id || "";

  useEffect(() => {
    setTab(initialTab);
  }, [initialTab]);

  useEffect(() => {
    window.scrollTo(0, 0);
    const current = SessionManager.getCurrentSession()?.[0];
    setSessionId(current?.session_id || "");
  }, []);

  const loadLookups = useCallback(async () => {
    const schoolCode = SessionManager.getSchoolCode();
    if (!schoolCode || !sessionId) return;
    try {
      const [classRes, staffRes, subRes] = await Promise.all([
        axios.get(`${API_BASE()}getClass.php`, {
          params: { school_code: schoolCode, session_id: sessionId },
        }),
        axios.get(`${API_BASE()}getStaff.php`, {
          params: { school_code: schoolCode },
        }),
        axios.get(`${API_BASE()}getQuestionBankClassSubjects.php`, {
          params: { school_code: schoolCode, session_id: sessionId },
        }),
      ]);
      setClassData(
        classRes.data?.api_status === "OK"
          ? classRes.data.data?.class || []
          : []
      );
      setStaffList(
        staffRes.data?.api_status === "OK" && Array.isArray(staffRes.data.staff)
          ? staffRes.data.staff
          : []
      );
      setClassSubjects(
        subRes.data?.api_status === "OK" && Array.isArray(subRes.data.data)
          ? subRes.data.data
          : []
      );
    } catch (err) {
      console.error(err);
    }
  }, [sessionId]);

  const loadData = useCallback(
    async ({ soft = false } = {}) => {
      const schoolCode = SessionManager.getSchoolCode();
      if (!schoolCode || !sessionId) {
        setLoading(false);
        return;
      }
      if (soft) setRefreshing(true);
      else setLoading(true);
      try {
        const planRes = await axios.get(`${API_BASE()}getLessonPlans.php`, {
          params: {
            school_code: schoolCode,
            session_id: sessionId,
            search: search.trim() || undefined,
          },
        });
        if (planRes.data?.api_status === "OK") {
          setPlans(Array.isArray(planRes.data.data) ? planRes.data.data : []);
          setCounts(planRes.data.counts || {});
        }

        const taskUrl =
          tab === "mine" && myStaffId
            ? `${API_BASE()}getMyLessonPlanTasks.php`
            : `${API_BASE()}getLessonPlanAssignments.php`;
        const taskParams = {
          school_code: schoolCode,
          session_id: sessionId,
        };
        if (tab === "mine" && myStaffId) {
          taskParams.assignee_staff_id = myStaffId;
        }
        const taskRes = await axios.get(taskUrl, { params: taskParams });
        if (taskRes.data?.api_status === "OK") {
          setTasks(Array.isArray(taskRes.data.data) ? taskRes.data.data : []);
        }
      } catch (err) {
        console.error(err);
        myToast.notify_warning("Failed to load lesson plan data.");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [sessionId, search, tab, myStaffId]
  );

  useEffect(() => {
    if (sessionId) loadLookups();
  }, [sessionId, loadLookups]);

  useEffect(() => {
    if (sessionId) loadData();
  }, [sessionId, tab, loadData]);

  const switchTab = (id) => {
    setTab(id);
    history.replace(`/AdminMain/LessonPlan?tab=${id}`);
  };

  const handleTaskAction = async (assignment, action) => {
    const schoolCode = SessionManager.getSchoolCode();
    if (!schoolCode) return;
    setBusyId(assignment.assignment_id);
    try {
      const res = await axios.post(`${API_BASE()}updateLessonPlanAssignment.php`, {
        school_code: schoolCode,
        assignment_id: assignment.assignment_id,
        action,
        reviewed_by: myStaffId,
      });
      if (res.data?.api_status === "OK") {
        myToast.notify_success("Updated.");
        loadData({ soft: true });
        if (action === "start" && assignment.task_type === "CREATE_PLAN") {
          history.push(
            `/AdminMain/LessonPlan/new?assignment_id=${assignment.assignment_id}`
          );
        }
      } else {
        myToast.notify_warning(res.data?.api_remark || "Update failed.");
      }
    } catch (err) {
      console.error(err);
      myToast.notify_warning("Update failed.");
    } finally {
      setBusyId("");
    }
  };

  const handleApprove = async (assignment) => {
    const schoolCode = SessionManager.getSchoolCode();
    if (!schoolCode || !assignment.plan_id) {
      myToast.notify_warning("No plan linked to approve.");
      return;
    }
    setBusyId(assignment.assignment_id);
    try {
      const res = await axios.post(`${API_BASE()}updateLessonPlanAssignment.php`, {
        school_code: schoolCode,
        assignment_id: assignment.assignment_id,
        action: "approve",
        plan_id: assignment.plan_id,
        reviewed_by: myStaffId,
      });
      if (res.data?.api_status === "OK") {
        myToast.notify_success("Approved.");
        loadData({ soft: true });
      }
    } catch (err) {
      myToast.notify_warning("Approve failed.");
    } finally {
      setBusyId("");
    }
  };

  if (!loggedIn) return <Redirect to="/Login" />;

  return (
    <div className="content payroll-page sm-page lp-page">
      <div
        className="content-header d-flex justify-content-between align-items-center"
        style={{ background: COLORS.primary_color_shade5 }}
      >
        <div className="leftside-content-header d-flex align-items-center gap-2">
          <button
            type="button"
            className="qb-home-btn"
            aria-label="Home"
            onClick={() => history.push("/AdminMain")}
          >
            <FaHome />
          </button>
          <ul className="breadcrumbs mb-0">
            <li>
              <button
                type="button"
                className="am-crumb"
                onClick={() => history.push("/AdminMain/Academic/1")}
              >
                Academic
              </button>
            </li>
            <li>
              <span style={{ color: COLORS.white }}>Lesson Plan</span>
            </li>
          </ul>
        </div>
        <div className="rightside-content-header">
          <BackButton />
        </div>
      </div>

      <div className="bg_page_white_rounded box_shadow animated fadeInUp sm-shell">
        <header className="sm-hero" style={{ "--sm-hero-bg": HERO_GRADIENT }}>
          <div className="sm-hero__brand">
            <div className="sm-hero__icon" aria-hidden>
              <FaClipboardList />
            </div>
            <div className="min-w-0">
              <h1 className="sm-hero__title">Lesson Plan</h1>
              <p className="sm-hero__sub">
                Create plans, assign staff tasks, track completion
              </p>
            </div>
          </div>
          <div className="sm-hero__tools">
            <button
              type="button"
              className="sj-btn sj-btn--primary"
              onClick={() => history.push("/AdminMain/LessonPlan/new")}
            >
              <FaPlus />
              <span>New plan</span>
            </button>
            <button
              type="button"
              className="sj-btn sj-btn--ghost"
              onClick={() => setAssignOpen(true)}
            >
              <FaTasks />
              <span>Assign task</span>
            </button>
            <button
              type="button"
              className={`sj-icon-btn${refreshing ? " is-spinning" : ""}`}
              onClick={() => loadData({ soft: true })}
              aria-label="Refresh"
            >
              <FaSyncAlt />
            </button>
          </div>
        </header>

        <div className="lp-stats">
          <div className="lp-stat">
            <span className="lp-stat__n">{counts.plans_total || 0}</span>
            <span className="lp-stat__l">Plans</span>
          </div>
          <div className="lp-stat">
            <span className="lp-stat__n">{counts.assignments_open || 0}</span>
            <span className="lp-stat__l">Open tasks</span>
          </div>
          <div className="lp-stat">
            <span className="lp-stat__n">{counts.assignments_submitted || 0}</span>
            <span className="lp-stat__l">Awaiting review</span>
          </div>
        </div>

        <div className="lp-tabs" role="tablist">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                type="button"
                role="tab"
                className={`lp-tab${tab === t.id ? " is-active" : ""}`}
                onClick={() => switchTab(t.id)}
              >
                <Icon aria-hidden />
                {t.label}
              </button>
            );
          })}
        </div>

        {tab === "plans" ? (
          <div className="lp-toolbar">
            <label className="lp-search">
              <FaSearch aria-hidden />
              <input
                type="search"
                placeholder="Search plans…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && loadData({ soft: true })}
              />
            </label>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={() => loadData({ soft: true })}
            >
              Search
            </button>
          </div>
        ) : null}

        {loading ? (
          <MyLoader />
        ) : tab === "plans" ? (
          <div className="lp-list">
            {plans.length === 0 ? (
              <div className="lp-empty">No lesson plans yet. Create your first plan.</div>
            ) : (
              plans.map((p) => (
                <article key={p.plan_id} className="lp-card">
                  <div className="lp-card__main">
                    <h3>{p.plan_title}</h3>
                    <p className="lp-card__meta">
                      {p.class_name} · {p.section_name} · {p.subject_name} ·{" "}
                      {p.staff_name}
                    </p>
                    {(p.chapter_name || p.topic_name) && (
                      <p className="lp-card__sub">
                        {[p.chapter_name, p.topic_name].filter(Boolean).join(" — ")}
                      </p>
                    )}
                    <p className="lp-card__date">
                      {p.plan_date || "No date"} · {p.duration_mins || 40} min
                    </p>
                  </div>
                  <div className="lp-card__side">
                    <StatusPill value={p.approval_status} />
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary"
                      onClick={() =>
                        history.push(`/AdminMain/LessonPlan/${p.plan_id}`)
                      }
                    >
                      <FaPencilAlt /> Edit
                    </button>
                  </div>
                </article>
              ))
            )}
          </div>
        ) : (
          <div className="lp-list">
            {tasks.length === 0 ? (
              <div className="lp-empty">
                {tab === "mine"
                  ? "No tasks assigned to you."
                  : "No assignments yet."}
              </div>
            ) : (
              tasks.map((t) => (
                <article key={t.assignment_id} className="lp-card">
                  <div className="lp-card__main">
                    <h3>{t.task_title}</h3>
                    <p className="lp-card__meta">
                      {t.assignee_name} · {t.task_type?.replace(/_/g, " ")}
                      {t.class_name ? ` · ${t.class_name}` : ""}
                      {t.subject_name ? ` · ${t.subject_name}` : ""}
                    </p>
                    <p className="lp-card__date">
                      Due: {t.due_date || "—"} · Priority: {t.priority}
                    </p>
                    {t.task_instructions ? (
                      <p className="lp-card__sub">{t.task_instructions}</p>
                    ) : null}
                  </div>
                  <div className="lp-card__side lp-card__actions">
                    <StatusPill value={t.task_status} />
                    {tab === "mine" && t.task_status === "Open" ? (
                      <button
                        type="button"
                        className="btn btn-sm btn-primary"
                        disabled={busyId === t.assignment_id}
                        onClick={() => handleTaskAction(t, "start")}
                      >
                        <FaPlay /> Start
                      </button>
                    ) : null}
                    {tab === "mine" && t.task_status === "In_Progress" ? (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary"
                        onClick={() =>
                          history.push(
                            `/AdminMain/LessonPlan/new?assignment_id=${t.assignment_id}`
                          )
                        }
                      >
                        Create plan
                      </button>
                    ) : null}
                    {tab === "tasks" && t.task_status === "Submitted" ? (
                      <button
                        type="button"
                        className="btn btn-sm btn-success"
                        disabled={busyId === t.assignment_id}
                        onClick={() => handleApprove(t)}
                      >
                        <FaCheck /> Approve
                      </button>
                    ) : null}
                    {t.plan_id ? (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() =>
                          history.push(`/AdminMain/LessonPlan/${t.plan_id}`)
                        }
                      >
                        View plan
                      </button>
                    ) : null}
                  </div>
                </article>
              ))
            )}
          </div>
        )}
      </div>

      <MdlLessonPlanAssign
        show={assignOpen}
        onClose={() => setAssignOpen(false)}
        onSaved={() => loadData({ soft: true })}
        sessionId={sessionId}
        staffList={staffList}
        classRows={flattenClasses(classData)}
        subjects={classSubjects}
      />

      <style>{`
        .lp-stats { display: flex; gap: 1rem; padding: 1rem 1.5rem 0; flex-wrap: wrap; }
        .lp-stat { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; padding: 0.75rem 1.1rem; min-width: 110px; }
        .lp-stat__n { display: block; font-size: 1.35rem; font-weight: 700; color: #0f172a; }
        .lp-stat__l { font-size: 0.78rem; color: #64748b; }
        .lp-tabs { display: flex; gap: 0.5rem; padding: 1rem 1.5rem 0; flex-wrap: wrap; }
        .lp-tab { display: inline-flex; align-items: center; gap: 0.4rem; border: 1px solid #e2e8f0; background: #fff; border-radius: 999px; padding: 0.45rem 0.95rem; font-size: 0.88rem; color: #475569; }
        .lp-tab.is-active { background: #0f172a; color: #fff; border-color: #0f172a; }
        .lp-toolbar { display: flex; gap: 0.5rem; padding: 1rem 1.5rem 0; align-items: center; }
        .lp-search { display: flex; align-items: center; gap: 0.5rem; flex: 1; max-width: 360px; border: 1px solid #e2e8f0; border-radius: 10px; padding: 0.35rem 0.75rem; background: #fff; }
        .lp-search input { border: 0; outline: 0; width: 100%; font-size: 0.9rem; }
        .lp-list { padding: 1rem 1.5rem 1.5rem; display: flex; flex-direction: column; gap: 0.75rem; }
        .lp-card { display: flex; justify-content: space-between; gap: 1rem; padding: 1rem 1.1rem; border: 1px solid #e8ecf4; border-radius: 14px; background: #fff; }
        .lp-card h3 { font-size: 1rem; font-weight: 700; margin: 0 0 0.35rem; color: #0f172a; }
        .lp-card__meta, .lp-card__date { margin: 0; font-size: 0.82rem; color: #64748b; }
        .lp-card__sub { margin: 0.35rem 0 0; font-size: 0.85rem; color: #334155; }
        .lp-card__side { display: flex; flex-direction: column; align-items: flex-end; gap: 0.5rem; flex-shrink: 0; }
        .lp-card__actions { min-width: 120px; }
        .lp-pill { display: inline-block; font-size: 0.72rem; font-weight: 700; padding: 0.2rem 0.55rem; border-radius: 999px; text-transform: capitalize; }
        .lp-empty { text-align: center; color: #64748b; padding: 2.5rem 1rem; }
        @media (max-width: 768px) {
          .lp-card { flex-direction: column; }
          .lp-card__side { align-items: flex-start; }
        }
      `}</style>
    </div>
  );
}
